import styled from "styled-components";
import { FaInfoCircle } from "react-icons/fa";

export const InfoIcon = styled(FaInfoCircle)`
  position: absolute;
  bottom: 8px;
  left: 8px;
  color: #999;
  cursor: help;
  font-size: 14px;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
`;

export const Card = styled.div`
  background-color: ${({ isHighlighted }) => isHighlighted ? '#fff3cd' : 'white'};
  border: 1px solid ${({ isHighlighted }) => isHighlighted ? '#ffeaa7' : '#e8e8e8'};
  border-radius: 12px;
  box-shadow: ${({ isHighlighted }) => isHighlighted ? '0 4px 12px rgba(255, 193, 7, 0.3)' : '0 2px 6px rgba(0, 0, 0, 0.08)'};
  padding: 12px 16px;
  width: 100%;
  text-align: center;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease-in-out;
  cursor: ${({ clickable }) => clickable ? 'pointer' : 'default'};
  
  ${({ isHighlighted }) => isHighlighted && `
    animation: highlightPulse 0.6s ease-in-out;
    transform: scale(1.02);
  `}

  @keyframes highlightPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 8px 20px rgba(255, 193, 7, 0.4);
    }
    100% {
      transform: scale(1.02);
      box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
    }
  }

  ${({ clickable }) => clickable && `
    &:hover {
      background-color: #f8f9fa;
      border-color: #d0d0d0;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    }
  `}
`;

export const Title = styled.h4`
  font-size: 14px;
  color: ${({ color }) => color || "#333"};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  text-align: center;
  font-weight: 600;
`;

export const Value = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: ${({ isHighlighted }) => isHighlighted ? '#856404' : '#333'};
  text-align: center;
  margin: 8px 0;
  transition: color 0.3s ease-in-out;
  
  ${({ isHighlighted }) => isHighlighted && `
    animation: valueGlow 1s ease-in-out;
  `}

  @keyframes valueGlow {
    0%, 100% {
      text-shadow: none;
    }
    50% {
      text-shadow: 0 0 8px rgba(133, 100, 4, 0.6);
    }
  }
`;

export const Percentage = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: #f1f1f1;
  color: #999;
  font-size: 8px;
  font-weight: normal;
  padding: 1px 3px;
  border-radius: 2px;
  box-shadow: none;
  opacity: 0.85;
`;

export const Details = styled.div`
  font-size: 9px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
  margin-top: 4px;
`;

export const DetailItem = styled.div`
  display: flex;
  align-items: start;
  color: ${({ color }) => color || "#333"};
  gap: 4px;
  margin-top: -10px;
`;

export const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 0; /* No gap since we have connectors between cards */
  min-width: max-content;
  flex-wrap: nowrap;
  margin-bottom: 10px;
  width: auto;
  overflow: visible;
  justify-content: center; /* Center the summary cards */
  position: relative;
  z-index: 10;
  padding: 0 20px; /* Add padding to prevent edge cutoff */
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px 20px;
`;

export const SkeletonCard = styled(Card)`
  background: #f5f5f5;
  padding: 12px 16px;
  min-height: 100px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  
  .ant-skeleton {
    width: 100%;
    text-align: center;
    
    .ant-skeleton-title {
      height: 14px !important;
      margin: 0 auto 8px auto !important;
    }
    
    .ant-skeleton-paragraph {
      margin: 8px auto 0 auto !important;
      text-align: center;
      
      li {
        height: 20px !important;
        margin: 0 auto !important;
        border-radius: 4px;
      }
    }
  }
`;

// Legacy styles for backward compatibility with old card components
export const SummaryCard = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: white;
  border: 1px solid #e0e0e0;
  margin-right: 16px;
  min-width: 160px;
  text-align: center;
`;

export const SummaryTitle = styled.h4`
  font-size: 12px;
  color: ${({ color }) => color || "#666"};
  margin-bottom: 8px;
  text-transform: uppercase;
  font-weight: 500;
`;

export const SummaryCount = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
`;

export const SummaryPercentage = styled.div`
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

export const SummaryPointer = styled.span`
  color: #1890ff;
  font-weight: 500;
`;

export const SummaryPointerWrapper = styled.div`
  margin-top: 8px;
`;

export const SummaryTitlesWrapper = styled.div`
  margin-bottom: 8px;
`;

// Flip card styles  
export const CardContainer = styled.div`
  width: 100%;
  min-height: 120px;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.6s;
  cursor: ${({ clickable }) => clickable ? 'pointer' : 'default'};
  
  &:hover {
    transform: ${({ hasRevenue }) => hasRevenue ? 'rotateY(180deg)' : 'none'};
  }
`;

export const CardFront = styled(Card)`
  position: absolute;
  width: 100%;
  min-height: 120px;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform: rotateY(0deg);
  top: 0;
  left: 0;
`;

export const CardBack = styled.div`
  position: absolute;
  width: 100%;
  min-height: 120px;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform: rotateY(180deg);
  background: linear-gradient(135deg, ${({ color }) => color || '#1890ff'}, ${({ color }) => color || '#1890ff'}dd);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  top: 0;
  left: 0;
`;

export const RevenueText = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  text-align: center;
  margin-top: 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;